import React from 'react'
import { Button } from './Button'

// ─── Global Error Boundary ────────────────────────────────────────────────────

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // In production this would go to an error reporting service.
    // We do NOT log user content — only the error message and stack.
    console.error('[ChangeLink Error Boundary]', error.message, errorInfo.componentStack)
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.694 0L3.19 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-lg font-semibold text-slate-900 mb-2">Something went wrong</h1>
            <p className="text-sm text-slate-500 mb-6">
              An unexpected error occurred. Please try again. If the problem persists, refresh the page.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => { window.location.reload() }}>
                Refresh page
              </Button>
              <Button onClick={this.handleRetry}>Try again</Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// ─── Route-level Error State ──────────────────────────────────────────────────

interface RouteErrorProps {
  title?: string
  description?: string
  onRetry?: () => void
}

export function RouteError({
  title = 'Could not load this page',
  description = 'Something went wrong loading this content. Please try again.',
  onRetry,
}: RouteErrorProps) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
      <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
        </svg>
      </div>
      <h2 className="text-base font-semibold text-slate-900 mb-1">{title}</h2>
      <p className="text-sm text-slate-500 mb-6 max-w-sm">{description}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>Try again</Button>
      )}
    </div>
  )
}

// ─── Status Banner ────────────────────────────────────────────────────────────

interface StatusBannerProps {
  status: 'draft' | 'approved' | 'declined' | 'sent' | 'viewed' | 'revision_requested' | 'superseded'
}

const bannerConfig = {
  draft: {
    bg: 'bg-amber-50 border-amber-200',
    text: 'text-amber-800',
    icon: '✏️',
    label: 'Draft — not yet sent for approval',
  },
  sent: {
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-800',
    icon: '📨',
    label: 'Sent — waiting for customer response',
  },
  viewed: {
    bg: 'bg-purple-50 border-purple-200',
    text: 'text-purple-800',
    icon: '👁️',
    label: 'Viewed — customer has opened the link',
  },
  approved: {
    bg: 'bg-emerald-50 border-emerald-200',
    text: 'text-emerald-800',
    icon: '✅',
    label: 'Approved and locked — version is final',
  },
  declined: {
    bg: 'bg-rose-50 border-rose-200',
    text: 'text-rose-800',
    icon: '❌',
    label: 'Declined — customer did not approve',
  },
  revision_requested: {
    bg: 'bg-orange-50 border-orange-200',
    text: 'text-orange-800',
    icon: '🔄',
    label: 'Revision requested — please update and resend',
  },
  superseded: {
    bg: 'bg-slate-100 border-slate-200',
    text: 'text-slate-600',
    icon: '📄',
    label: 'Superseded — replaced by a newer version',
  },
}

export function StatusBanner({ status }: StatusBannerProps) {
  const config = bannerConfig[status]
  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium ${config.bg} ${config.text}`}
      role="status"
    >
      <span aria-hidden="true">{config.icon}</span>
      {config.label}
    </div>
  )
}
